'use client';

import { GoogleAds, useScreenDetect } from '@sgcmpteltd/uikit-next';
import type {
  OnSlotRenderEnded,
} from '@sgcmpteltd/uikit-next/components/google-ads';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getMyAlertData } from '@/actions/new-car/alert-list/get';
import type {
  CamelCaseAdAlert,
  CamelCaseAdAlertList,
  CamelCaseCarAlert,
  CamelCaseCarAlertList,
  CamelCaseResponseData as CamelCaseMyAlert,
} from '@/apis/server/new-car/v1/my-alert';
import { isCamelCaseCarAlert } from '@/apis/server/new-car/v1/my-alert/utils';
import {
  SearchFilterSection,
} from '@/app/_components/page_content/search-filter-section';
import type {
  AlertList,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card';
import {
  getPriceRangeText,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card';
import { NewCarBreadcrumb as Breadcrumb } from '@/components/breadcrumb';
import { DynamicEyeotaScript } from '@/components/dynamic-eyeota-script';
import { Leaderboard } from '@/components/leaderboard';
import { eyeotaPid } from '@/configs/analytics';
import {
  newCarListingLeaderboard as dynamicYieldsListingLeaderboard,
} from '@/configs/dynamic-yields/ads/desktop';
import {
  newCarInfoLeaderboard as googleAdsInfoLeaderboard,
} from '@/configs/google/ads/desktop';
import { infoBanner } from '@/configs/google/ads/mobile';
import { PARALLEL_IMPORTERS } from '@/constants/global';
import { AppContext } from '@/context/app';
import { InfoContext } from '@/context/info';

import { GalleryViewer } from '../page-content/gallery-viewer';
import { getMinPrice, getPriceRange } from '../page-content/utils';
import { breadcrumbList, createEyeotaAJSScriptTargetMapping } from './configs';
import { NavTabHeader } from './nav-tab-header';
import { LINK_KEY_OVERVIEW } from './nav-tab-header/constants';
import type { LinkKey } from './nav-tab-header/types';
import styles from './styles.module.scss';
import { ThumbnailsNavigator } from './thumbnails-navigator';
import { TitleAndQuickButton } from './title-and-quick-button';
import type { Component } from './types';
import {
  carDetailDataLayer,
  carDetailTargetMapping,
  linkKeyMap,
} from './utils';

export const PageLayout: Component = ({
  brandList,
  carDetail,
  tacticalAds,
  makeArticles,
  otherArticles,
  dyArticles,
  children,
  myAlertList = null,
}) => {
  const [
    myAlert,
    setMyAlert,
  ] = useState<CamelCaseMyAlert | null>(myAlertList);
  const pathname = usePathname();
  const { isDesktop, isIpadMini } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const isIpadMiniMode = isIpadMini();
  const { Provider: InfoContextProvider } = InfoContext;
  const searchParams = useSearchParams();
  const [
    selectedSubmodelId,
    setSelectedSubmodelId,
  ] = useState<number | null>(null);
  // it might be undefined which means default overview
  const segment = linkKeyMap(pathname.split('/')[4]) as LinkKey;
  const [galleryViewerSlide, setGalleryViewerSlide] = useState(0);
  const [isGalleryViewerOpen, setIsGalleryViewerOpen] = useState(false);
  const appContextData = useContext(AppContext);

  useEffect(() => {
    if (myAlertList === null) {
      getMyAlertData().then((result) => {
        if (result.status === ACTION_STATUS_SUCCESS) {
          const res = result.data.data;

          setMyAlert(res);
        }
      });
    }
  }, [myAlertList]);

  useEffect(() => {
    const submodelId = searchParams.get('submodel_id');

    if (submodelId) {
      setSelectedSubmodelId(+submodelId);
    }
  }, [searchParams]);

  const submodelMinPrice = getMinPrice(carDetail.subModels);
  const submodelPriceRange = getPriceRange(carDetail.subModels);

  let priceRangeText;

  if (carDetail.additionalProperties.isUpcoming) {
    priceRangeText = 'TBA';
  } else if (carDetail.dealerType === 'pi'
  && submodelMinPrice
  && !carDetail.additionalProperties.isSpotlight) {
    const roundedPrice = Math.ceil(submodelMinPrice / 100) * 100;

    priceRangeText = `$${roundedPrice.toLocaleString('en-US')}`;
  } else if ((carDetail.dealerType === 'ad'
  || carDetail.additionalProperties.isSpotlight)
  && submodelPriceRange) {
    priceRangeText = getPriceRangeText(submodelPriceRange);
  } else {
    priceRangeText = 'POA';
  }

  const onSlotRenderEndedRef = useRef<OnSlotRenderEnded>(({
    isEmpty,
    slot,
  }) => {
    if (!isEmpty) {
      return;
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh([
        slot,
      ]);
    });
  });

  const handleAlertModalSubmit = (
    alert: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    if (isCamelCaseCarAlert(alert) && myAlert) {
      if (!alert.adCarPriceChange && !alert.piCarPromo) {
        const newCarAlert = myAlert.carAlerts
          .filter(carAlert => carAlert.carId !== alert.carId);

        if (newCarAlert) {
          setMyAlert({ ...myAlert, carAlerts: newCarAlert });
        }
      } else {
        setMyAlert({
          ...myAlert,
          carAlerts: [...myAlert.carAlerts, alert],
        });
      }
    }
  };

  let adAlerts: CamelCaseAdAlertList = [];
  let carAlerts: CamelCaseCarAlertList = [];

  if (myAlert) {
    ({ adAlerts, carAlerts } = myAlert);
  }

  const matchingAdAlerts = adAlerts.filter(
    alert => alert.make === carDetail.make,
  );

  const matchingCarAlerts = carAlerts.filter(
    alert => alert.carId === carDetail.id,
  );
  const isShortlisted = matchingCarAlerts.length > 0
    || matchingAdAlerts.length > 0;
  const alertList: AlertList = {
    adAlert: matchingAdAlerts.length
      ? matchingAdAlerts[0]
      : {
        make: carDetail.make,
        adPromo: false,
        adCarLaunches: false,
      },
    carAlert: matchingCarAlerts.length
      ? matchingCarAlerts[0]
      : {
        carId: carDetail.id,
        adCarPriceChange: false,
        piCarPromo: false,
      },
  };

  const photos = useMemo(() => [
    ...carDetail.imageNums
      .filter(image => (!!carDetail.subModels
        .find(submodel => ((submodel.id === image.subModelId)
        && submodel.isAvailable)
        || image.subModelId === 0)))
      .map(image => ({
        name: `${carDetail.make} ${carDetail.model} ${carDetail.subModels
          .find(submodel => submodel.id === image.subModelId)?.name ?? ''}`,
        url: `https://i.i-sgcm.com/new_cars/cars/${carDetail.id}/${carDetail.id}_g${image.imageNum}`,
      })),
  ], [
    carDetail.id,
    carDetail.imageNums,
    carDetail.subModels,
    carDetail.make,
    carDetail.model,
  ]);

  const handleFilterClick = () => {
    appContextData?.setIsFilterModalOpen(true);
  };

  const handleThumbnailsNavigatorBoxClick = (index: number) => {
    setIsGalleryViewerOpen(true);
    setGalleryViewerSlide(index);
  };

  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push(carDetailDataLayer(carDetail));
    }
  }, [carDetail]);

  return (
    <InfoContextProvider value={{
      selectedSubmodelId,
      setSelectedSubmodelId,
      carDetail,
      tacticalAds,
      makeArticles,
      otherArticles,
      dyArticles,
    }}>
      <DynamicEyeotaScript
        pid={eyeotaPid ?? ''}
        targetMapping={createEyeotaAJSScriptTargetMapping(carDetail)} />
      <GalleryViewer
        images={photos}
        initialSideIndex={galleryViewerSlide}
        isOpen={isGalleryViewerOpen}
        onClose={() => setIsGalleryViewerOpen(false)} />
      <Leaderboard
        className={styles.containerLeaderboard}
        dyAdsConfig={dynamicYieldsListingLeaderboard}
        googleAdsConfig={{
          ...googleAdsInfoLeaderboard,
          targetingMap: carDetailTargetMapping(carDetail),
        }}
        googleAdsProbability={0.8} />
      <div className={styles.containerBreadcrumb}>
        <Breadcrumb
          list={breadcrumbList(`${carDetail.make} ${carDetail.model}`)} />
      </div>
      {
        isDesktopMode && (
          <div
            className={styles.containerSearchFilter}>
            <SearchFilterSection
              brandList={brandList}
              defaultSearchData={{ query: {} }}
              isFilterModalOpen={appContextData?.isFilterModalOpen ?? false}
              onFilterClick={handleFilterClick} />
          </div>
        )
      }
      {!isIpadMiniMode && (
        <ThumbnailsNavigator
          images={photos.map(photo => photo.url)}
          onBoxClick={handleThumbnailsNavigatorBoxClick} />
      )}
      <TitleAndQuickButton
        alertList={alertList}
        carDetail={carDetail}
        isPi={carDetail.parallelDealers !== null}
        isShortlistOnClick={isShortlisted}
        subTitle={` ${carDetail.dealerType === PARALLEL_IMPORTERS ? '(Parallel Imported)' : ''}`}
        title={`${carDetail.make} ${carDetail.model}`}
        onAlertModalSubmit={handleAlertModalSubmit} />
      {!isIpadMiniMode
      && (
        <div className={
          styles.textPrice
        }>
          {priceRangeText}
        </div>
      )}
      {!isDesktopMode && (
        <div className={styles.containerAds}>
          <GoogleAds
            adUnitPath={infoBanner.adUnitPath}
            divId={infoBanner.divId}
            slot={infoBanner.slot}
            targetingMap={carDetailTargetMapping(carDetail)}
            onSlotRenderEndedRef={onSlotRenderEndedRef} />
        </div>
      )}
      <NavTabHeader
        activeTab={segment ?? LINK_KEY_OVERVIEW}
        carDetail={carDetail}
        prefix={`/info/${carDetail.id}/${carDetail.slug}`} />
      {children}
    </InfoContextProvider>
  );
};
